import React, { useEffect } from "react";
import { Command } from "cmdk";
import { motion, AnimatePresence } from "framer-motion";
import { Home, User, FolderGit2, Cpu, Github, Briefcase, Mail, FileDown, Linkedin, Sun, Moon, Terminal } from "lucide-react";
import { useTheme } from "../lib/api";

export default function CommandPalette({ open, setOpen, config }) {
    const { toggle } = useTheme();
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [setOpen]);

    const nav = (href) => { setOpen(false); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };
    const ext = (url) => { if (url) window.open(url, "_blank"); setOpen(false); };

    const items = [
        {
            group: "Navigate", cmds: [
                { icon: Home, label: "Home", action: () => { setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); } },
                { icon: User, label: "About", action: () => nav("#about") },
                { icon: FolderGit2, label: "Projects", action: () => nav("#projects") },
                { icon: Cpu, label: "Skills", action: () => nav("#skills") },
                { icon: Github, label: "GitHub Stats", action: () => nav("#github") },
                { icon: Briefcase, label: "Experience", action: () => nav("#experience") },
                { icon: Mail, label: "Contact", action: () => nav("#contact") },
            ]
        },
        {
            group: "Links", cmds: [
                { icon: Github, label: "GitHub Profile", action: () => ext(config?.socials?.github) },
                { icon: Linkedin, label: "LinkedIn Profile", action: () => ext(config?.socials?.linkedin) },
                { icon: FileDown, label: "Download Résumé", action: () => ext(config?.resume_url || "/Resume.pdf") },
            ]
        },
        {
            group: "Actions", cmds: [
                { icon: Sun, label: "Toggle theme", action: () => { toggle(); setOpen(false); } },
                { icon: Terminal, label: "Admin Panel", action: () => { window.location.href = "/admin"; } },
            ]
        },
    ];

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[90] flex items-start justify-center pt-[15vh] px-4 bg-black/50 backdrop-blur-sm"
                    onClick={() => setOpen(false)}>
                    <motion.div initial={{ scale: 0.96, y: -10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()} className="w-full max-w-lg glass rounded-2xl overflow-hidden shadow-2xl gradient-border">
                        <Command data-testid="command-palette" className="[&_[cmdk-input]]:outline-none">
                            <div className="flex items-center gap-3 px-4 border-b border-border">
                                <Command.Input autoFocus placeholder="Type a command or search…" data-testid="command-input"
                                    className="w-full bg-transparent py-4 text-sm outline-none placeholder:text-muted-foreground" />
                                <kbd className="font-mono text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">ESC</kbd>
                            </div>
                            <Command.List className="max-h-[50vh] overflow-y-auto p-2">
                                <Command.Empty className="py-8 text-center text-sm text-muted-foreground">No results found.</Command.Empty>
                                {items.map((g) => (
                                    <Command.Group key={g.group} heading={g.group} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
                                        {g.cmds.map((c) => (
                                            <Command.Item key={c.label} onSelect={c.action} data-testid={`cmd-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer aria-selected:bg-secondary aria-selected:text-primary transition-colors">
                                                <c.icon className="w-4 h-4" /> {c.label}
                                            </Command.Item>
                                        ))}
                                    </Command.Group>
                                ))}
                            </Command.List>
                        </Command>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
